import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import * as qrCodeService from "@/shared/api/services/qrCodeService";
import * as adminService from "@/shared/api/services/adminService";
import TransactionFilters from "@/shared/components/transactions/TransactionFilters";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import TransactionDetail from "@/shared/components/transactions/TransactionDetail";
import { exportToCsv } from "@/shared/utils/csvExport";
import { ChevronLeft, ChevronRight, ArrowDownToLine, Activity, Sparkles, Layers } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

/* ═══════════════════════════════════════════════════════════════════
   GLOBAL CSS  (dep-* prefix, injected once)
   Overrides the TransactionDetail sheet + table/filter components
   so they all share the same dark cyan design language.
═══════════════════════════════════════════════════════════════════ */
const DEP_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

/* ── page shell ── */
.dep{display:flex;flex-direction:column;gap:1.25rem;background:#060C18;min-height:100%;padding:1.25rem;font-family:'Outfit',system-ui,sans-serif}
.dep *{font-family:'Outfit',system-ui,sans-serif !important}

/* ── nuclear dark override for shared components ── */
.dep .bg-white,.dep .bg-slate-50,.dep .bg-slate-100,.dep .bg-gray-50,.dep [class*="bg-white"]{background:#0E1C35 !important}
.dep .text-slate-900,.dep .text-slate-800,.dep .text-gray-900{color:#EFF6FF !important}
.dep .text-slate-600,.dep .text-slate-500,.dep .text-gray-500{color:#93B4D4 !important}
.dep .text-slate-400{color:#4D6E90 !important}
.dep .border-slate-200,.dep .border-slate-100{border-color:rgba(255,255,255,0.07) !important}
.dep .shadow-sm,.dep .shadow{box-shadow:none !important}

/* ── header ── */
.dep-hdr{display:flex;flex-wrap:wrap;align-items:flex-start;justify-content:space-between;gap:1rem}
.dep-hdr-l{display:flex;flex-direction:column;gap:0.3rem}
.dep-live{display:flex;align-items:center;gap:0.45rem}
.dep-dot-wrap{position:relative;width:0.55rem;height:0.55rem}
.dep-dot{position:absolute;inset:0;border-radius:50%;background:#3B82F6}
.dep-dot-ring{position:absolute;inset:0;border-radius:50%;background:#3B82F6;animation:dep-ping 2s ease-in-out infinite}
@keyframes dep-ping{0%,100%{transform:scale(1);opacity:.5}50%{transform:scale(2);opacity:0}}
.dep-live-lbl{font-size:0.58rem;font-weight:800;letter-spacing:0.18em;text-transform:uppercase;color:#4D6E90}
.dep-title{font-size:1.5rem;font-weight:800;color:#EFF6FF;letter-spacing:-0.02em;margin:0}
.dep-count{font-size:0.78rem;font-weight:500;color:#4D6E90;margin:0}
.dep-count b{color:#22D3EE}

/* ── stat chips ── */
.dep-chips{display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap}
.dep-chip{display:flex;align-items:center;gap:0.45rem;padding:0.35rem 0.875rem;border-radius:0.625rem;border:1px solid transparent;cursor:default;transition:background 120ms,border-color 120ms}
.dep-chip-ico{width:0.8rem;height:0.8rem;flex-shrink:0}
.dep-chip-lbl{font-size:0.6rem;font-weight:800;letter-spacing:0.16em;text-transform:uppercase}
.dep-chip-val{font-size:0.8125rem;font-weight:700}
.dep-chip-c{background:rgba(34,211,238,0.08);border-color:rgba(34,211,238,0.18)}
.dep-chip-c .dep-chip-lbl{color:rgba(34,211,238,0.6)}.dep-chip-c .dep-chip-val{color:#22D3EE}
.dep-chip-c:hover{background:rgba(34,211,238,0.13);border-color:rgba(34,211,238,0.28)}
.dep-chip-e{background:rgba(16,185,129,0.08);border-color:rgba(16,185,129,0.18)}
.dep-chip-e .dep-chip-lbl{color:rgba(16,185,129,0.6)}.dep-chip-e .dep-chip-val{color:#34D399}
.dep-chip-e:hover{background:rgba(16,185,129,0.13);border-color:rgba(16,185,129,0.28)}
.dep-chip-p{background:rgba(251,191,36,0.08);border-color:rgba(251,191,36,0.18)}
.dep-chip-p .dep-chip-lbl{color:rgba(251,191,36,0.6)}.dep-chip-p .dep-chip-val{color:#FBBF24}
.dep-chip-p:hover{background:rgba(251,191,36,0.13);border-color:rgba(251,191,36,0.28)}

/* ── panels ── */
.dep-panel{background:#0E1C35;border:1px solid rgba(255,255,255,0.07);border-radius:0.875rem;overflow:hidden;transition:border-color 150ms}
.dep-panel:hover{border-color:rgba(34,211,238,0.14)}
.dep-panel-bar{display:flex;align-items:center;justify-content:space-between;padding:0.75rem 1rem;border-bottom:1px solid rgba(255,255,255,0.06)}
.dep-panel-bar-l{display:flex;align-items:center;gap:0.55rem}
.dep-panel-icon{width:1.6rem;height:1.6rem;border-radius:0.4rem;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.dep-panel-icon-c{background:rgba(34,211,238,0.08);border:1px solid rgba(34,211,238,0.16)}
.dep-panel-icon-b{background:rgba(59,130,246,0.08);border:1px solid rgba(59,130,246,0.16)}
.dep-panel-icon svg{width:0.8rem;height:0.8rem}
.dep-panel-icon-c svg{color:#22D3EE}.dep-panel-icon-b svg{color:#60A5FA}
.dep-panel-title{font-size:0.65rem;font-weight:800;letter-spacing:0.1em;text-transform:uppercase;color:#4D6E90}
.dep-line{flex:1;height:1px;background:linear-gradient(90deg,rgba(34,211,238,0.10),transparent);margin-left:0.25rem}

/* ── export button ── */
.dep-export-btn{display:inline-flex;align-items:center;gap:0.3rem;padding:0 0.75rem;height:1.875rem;border-radius:0.5rem;font-size:0.72rem;font-weight:700;background:rgba(34,211,238,0.08);border:1px solid rgba(34,211,238,0.18);color:#22D3EE;cursor:pointer;transition:all 120ms}
.dep-export-btn svg{width:0.72rem;height:0.72rem}
.dep-export-btn:hover{background:rgba(34,211,238,0.14);border-color:rgba(34,211,238,0.32)}

/* ── sync badge ── */
.dep-sync{display:inline-flex;align-items:center;gap:0.3rem;padding:0.2rem 0.55rem;border-radius:0.4rem;background:rgba(34,211,238,0.07);border:1px solid rgba(34,211,238,0.14)}
.dep-sync-ico{width:0.65rem;height:0.65rem;color:#22D3EE;animation:dep-spin 1s linear infinite}
@keyframes dep-spin{to{transform:rotate(360deg)}}
.dep-sync-lbl{font-size:0.6rem;font-weight:800;color:#22D3EE}

/* ── filters panel inner ── */
.dep-filters-pad{padding:0.875rem 1rem}
.dep-filters input,.dep-filters [type="text"]{background:#0A1628 !important;border:1px solid rgba(255,255,255,0.09) !important;color:#EFF6FF !important;border-radius:0.55rem !important;transition:border-color 120ms !important}
.dep-filters input::placeholder{color:#4D6E90 !important}
.dep-filters input:focus{border-color:#22D3EE !important;box-shadow:0 0 0 2px rgba(34,211,238,0.14) !important;outline:none !important}
.dep-filters button[role="combobox"],.dep-filters [data-radix-select-trigger]{background:#0A1628 !important;border:1px solid rgba(255,255,255,0.09) !important;color:#EFF6FF !important;border-radius:0.55rem !important;transition:all 120ms !important}
.dep-filters button[role="combobox"]:hover,.dep-filters [data-radix-select-trigger]:hover{border-color:rgba(34,211,238,0.3) !important;background:#122040 !important;color:#22D3EE !important}
.dep-filters button:not([role="combobox"]){background:#0A1628 !important;border:1px solid rgba(255,255,255,0.09) !important;color:#93B4D4 !important;border-radius:0.55rem !important;transition:all 120ms !important}
.dep-filters button:not([role="combobox"]):hover{background:#122040 !important;border-color:rgba(34,211,238,0.3) !important;color:#22D3EE !important}

/* ── table ── */
.dep-table-wrap,.dep-table-wrap>*,.dep-table-wrap [class*="rounded"],.dep-table-wrap .bg-white,.dep-table-wrap .bg-slate-50{background:transparent !important}
.dep-table-wrap table{border-collapse:collapse !important;width:100% !important}
.dep-table-wrap thead,.dep-table-wrap thead tr,.dep-table-wrap thead th{background:rgba(255,255,255,0.02) !important}
.dep-table-wrap th{color:#3E5E7E !important;font-size:10px !important;font-weight:800 !important;letter-spacing:0.14em !important;text-transform:uppercase !important;border-bottom:1px solid rgba(255,255,255,0.06) !important;padding:11px 16px !important;white-space:nowrap !important}
.dep-table-wrap tbody tr{background:transparent !important;border-bottom:1px solid rgba(255,255,255,0.04) !important;transition:background 100ms !important;cursor:pointer !important}
.dep-table-wrap tbody tr:last-child{border-bottom:none !important}
.dep-table-wrap tbody tr:hover{background:rgba(34,211,238,0.045) !important}
.dep-table-wrap tbody tr:hover td:first-child{border-left:3px solid #22D3EE !important}
.dep-table-wrap td{background:transparent !important;padding:12px 16px !important;font-size:13px !important;font-weight:500 !important;vertical-align:middle !important;transition:color 100ms !important}
.dep-table-wrap td,.dep-table-wrap td *{color:#93B4D4 !important}
.dep-table-wrap tbody tr:hover td,.dep-table-wrap tbody tr:hover td *{color:#EFF6FF !important}
.dep-table-wrap .font-medium,.dep-table-wrap .font-semibold,.dep-table-wrap .font-bold{color:#B8D4EE !important}

/* status badges in table */
.dep-table-wrap [class*="bg-emerald"],.dep-table-wrap [class*="bg-green"]{background:rgba(16,185,129,0.12) !important;color:#34D399 !important;border:1px solid rgba(16,185,129,0.25) !important;border-radius:6px !important;font-size:11px !important;font-weight:700 !important}
.dep-table-wrap [class*="bg-amber"],.dep-table-wrap [class*="bg-yellow"]{background:rgba(251,191,36,0.12) !important;color:#FBBF24 !important;border:1px solid rgba(251,191,36,0.25) !important;border-radius:6px !important;font-size:11px !important;font-weight:700 !important}
.dep-table-wrap [class*="bg-red"]{background:rgba(251,113,133,0.12) !important;color:#FB7185 !important;border:1px solid rgba(251,113,133,0.25) !important;border-radius:6px !important;font-size:11px !important;font-weight:700 !important}
.dep-table-wrap [class*="bg-blue"]{background:rgba(59,130,246,0.12) !important;color:#60A5FA !important;border:1px solid rgba(59,130,246,0.25) !important;border-radius:6px !important;font-size:11px !important;font-weight:700 !important}
.dep-table-wrap [class*="bg-slate"]{background:rgba(100,116,139,0.12) !important;color:#94A3B8 !important;border:1px solid rgba(100,116,139,0.25) !important;border-radius:6px !important;font-size:11px !important;font-weight:700 !important}
.dep-table-wrap button:not([disabled]){background:rgba(34,211,238,0.07) !important;border:1px solid rgba(34,211,238,0.16) !important;color:#22D3EE !important;border-radius:6px !important;font-size:12px !important;font-weight:600 !important;transition:all 100ms !important}
.dep-table-wrap button:not([disabled]):hover{background:rgba(34,211,238,0.13) !important;border-color:rgba(34,211,238,0.32) !important;color:#67E8F9 !important}
.dep-table-wrap button svg{color:inherit !important}
.dep-table-wrap::-webkit-scrollbar,.dep-panel::-webkit-scrollbar{width:4px;height:4px}
.dep-table-wrap::-webkit-scrollbar-track,.dep-panel::-webkit-scrollbar-track{background:transparent}
.dep-table-wrap::-webkit-scrollbar-thumb,.dep-panel::-webkit-scrollbar-thumb{background:rgba(34,211,238,0.18);border-radius:99px}

/* ── TransactionDetail Sheet override ── */
/* These rules make the TransactionDetail panel look exactly like the
   updated version we built — dark navy background, cyan accents,
   proper status colours (red for failed/declined, green for success) */
.td-sheet,[data-radix-sheet-content]{
  background:#060C18 !important;
  border-left:1px solid rgba(255,255,255,0.07) !important;
}
.td-sheet *{font-family:'Outfit',system-ui,sans-serif !important}
.td-sheet .bg-white,.td-sheet .bg-slate-50,.td-sheet .bg-slate-100{background:#0E1C35 !important}
.td-sheet .text-slate-900,.td-sheet .text-slate-800{color:#EFF6FF !important}
.td-sheet .text-slate-500,.td-sheet .text-slate-600{color:#93B4D4 !important}
.td-sheet .border-slate-200{border-color:rgba(255,255,255,0.07) !important}
/* status colour helpers: success=green, failed/declined=red */
.td-sheet [class*="text-green"],[class*="text-emerald"]{color:#34D399 !important}
.td-sheet [class*="text-red"],[class*="text-rose"]{color:#FB7185 !important}

/* ── pagination ── */
.dep-pag{display:flex;align-items:center;justify-content:space-between;background:#0E1C35;border:1px solid rgba(255,255,255,0.07);border-radius:0.75rem;padding:0.55rem 0.875rem}
.dep-pag-info{font-size:0.775rem;color:#4D6E90}
.dep-pag-info b{color:#93B4D4}.dep-pag-info em{color:#22D3EE;font-style:normal;font-weight:700}
.dep-pag-ctrl{display:flex;align-items:center;gap:0.35rem}
.dep-pag-btn{width:1.875rem;height:1.875rem;border-radius:0.45rem;display:flex;align-items:center;justify-content:center;background:#122040;border:1px solid rgba(255,255,255,0.09);color:#93B4D4;cursor:pointer;transition:all 100ms}
.dep-pag-btn svg{width:0.8rem;height:0.8rem}
.dep-pag-btn:hover:not(:disabled){background:rgba(34,211,238,0.10);border-color:rgba(34,211,238,0.35);color:#22D3EE}
.dep-pag-btn:disabled{opacity:0.28;cursor:not-allowed}
.dep-pag-pill{width:1.875rem;height:1.875rem;border-radius:0.4rem;display:flex;align-items:center;justify-content:center;font-size:0.72rem;font-weight:700;border:1px solid rgba(255,255,255,0.09);background:#122040;color:#93B4D4;cursor:pointer;transition:all 100ms}
.dep-pag-pill:hover{background:rgba(34,211,238,0.08);border-color:rgba(34,211,238,0.25);color:#22D3EE}
.dep-pag-pill.active{background:#22D3EE;border-color:#22D3EE;color:#060C18;cursor:default}
/* ── Filter Section Dropdown Dark Navy + Cyan Hover ── */
.dep-filters button[role="combobox"],
.dep-filters [data-radix-select-trigger] {
  background: #0A1628;             /* dark navy */
  border: 1px solid rgba(255,255,255,0.09);
  color: #EFF6FF;
  border-radius: 0.55rem;
  transition: all 120ms;
}

/* ───────── Filter Dropdown Trigger (Closed State) ───────── */
.dep-filters button[role="combobox"],
.dep-filters [data-radix-select-trigger] {
  background: #0B1A2F !important;      /* deep navy */
  border: 1px solid #1E293B !important;
  color: #E2E8F0 !important;
  border-radius: 10px !important;
  height: 38px !important;
  padding: 0 12px !important;
  transition: all 0.18s ease !important;
}

/* Hover */
.dep-filters button[role="combobox"]:hover,
.dep-filters [data-radix-select-trigger]:hover {
  border-color: #22D3EE !important;    /* cyan border */
  color: #22D3EE !important;
  background: #0F223F !important;
}

/* Focus */
.dep-filters button[role="combobox"]:focus,
.dep-filters [data-radix-select-trigger]:focus {
  border-color: #22D3EE !important;
  box-shadow: 0 0 0 2px rgba(34,211,238,0.25) !important;
  outline: none !important;
}

/* ───────── Dropdown Menu Box ───────── */
[data-radix-select-content] {
  background: #0B1A2F !important;
  border: 1px solid #1E293B !important;
  border-radius: 12px !important;
  padding: 6px !important;
  box-shadow: 0 15px 40px rgba(0,0,0,0.6) !important;
}

/* ───────── Dropdown Items ───────── */
[data-radix-select-item] {
  background: transparent !important;
  color: #94A3B8 !important;
  border-radius: 8px !important;
  padding: 8px 12px !important;
  font-size: 13px !important;
  transition: all 0.15s ease !important;
}

/* Hover */
[data-radix-select-item]:hover,
[data-radix-select-item][data-highlighted] {
  background: rgba(34,211,238,0.12) !important;
  color: #22D3EE !important;
}

/* Selected */
[data-radix-select-item][data-state="checked"] {
  background: rgba(34,211,238,0.18) !important;
  color: #67E8F9 !important;
  font-weight: 600 !important;
}
`;

const PAGE_SIZE = 20;
const RESET_F = {
  search: "",
  status: "all",
  dateFrom: undefined,
  dateTo: undefined
};

export default function Deposits() {
  const { t } = useLanguage();
  const [filters, setFilters] = useState(RESET_F);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);

  /* Inject CSS once */
  useEffect(() => {
    const id = "dep-global-css";
    if (document.getElementById(id)) return;
    const el = Object.assign(document.createElement("style"), { id, textContent: DEP_CSS });
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);

  const filtersMapper = React.useMemo(() => ({
    status: filters.status && filters.status !== "all" ? filters.status : undefined,
    startDate: filters.dateFrom ? new Date(filters.dateFrom).toISOString() : undefined,
    endDate: filters.dateTo ? new Date(filters.dateTo).toISOString() : undefined,
  }), [filters]);

  const merchantIds = filters.merchant && filters.merchant !== "all" ? [filters.merchant] : null;

  const { data: qrCodesData, isLoading: qrLoading, refetch: refetchQr, error: qrError } = useQuery({
    queryKey: ["merchant-deposits", page, PAGE_SIZE, filters.search, filtersMapper, merchantIds],
    queryFn: () => qrCodeService.getMerchantQRCodesPagination(page + 1, PAGE_SIZE, filters.search, filtersMapper, merchantIds),
  });

  const { data: providers = [] } = useQuery({ queryKey: ["providers"], queryFn: async () => [] });
  const { data: merchants = [] } = useQuery({ queryKey: ["merchants"], queryFn: () => adminService.getAllMerchants() });

  const qrCodes = qrCodesData?.data || [];
  const totalTransactions = qrCodesData?.total || 0;
  const totalPages = qrCodesData?.totalPages || 1;

  // Normalize Deposits
  const transactions = React.useMemo(() => {
    return qrCodes.map((q) => ({
      id: q.id ?? q.payment_id,
      payment_id: q.payment_id ?? "",
      reference: q.reference ?? q.merchant_ref ?? "",
      amount: Number(q.amount ?? 0),
      net_amount: Number(q.net_amount ?? 0),
      total_fee: Number(q.total_fee ?? 0),
      status: (q.status ?? "pending").toLowerCase(),
      currency: q.currency,
      merchant_id: q.merchant.id,
      merchant_code: q.merchant_code,
      merchant_name: q.merchant.name,
      merchant: q.merchant,
      reseller_id: q.reseller_id,
      reseller_code: q.reseller_code,
      reseller_name: q.reseller_name,
      reseller: q.reseller,
      provider_code: q.provider_code,
      client_bank_name: q.client_bank_name,
      client_name: q.client_name,
      client_bank_account: q.client_bank_account,
      expires_at: q.expires_at,
      created_date: q.createdAt ?? "",
      updated_date: q.updatedAt ?? "",
      type: "DEPOSIT",
      _raw: q,
    }));
  }, [qrCodes]);

  const paginated = transactions;

  const stats = useMemo(() => {
    const success = transactions.filter(t => t.status === "success");
    const pending = transactions.filter(t => t.status === "pending" || t.status === "processing");
    return {
      total: success.reduce((s, t) => s + (t.amount || 0), 0),
      count: totalTransactions,
      successCount: success.length,
      pendingCount: pending.length
    };
  }, [transactions, totalTransactions]);

  const pills = useMemo(() => {
    const start = Math.max(0, Math.min(page - 2, totalPages - 5));
    return Array.from({ length: Math.min(totalPages, 5) }, (_, i) => start + i);
  }, [page, totalPages]);

  const handleFilter = useCallback((f) => { setFilters(f); setPage(0); }, []);
  const handleReset = useCallback(() => { setFilters(RESET_F); setPage(0); }, []);
  const handleExport = useCallback(() => {
    const HEADERS = ["id", "payment_id", "reference", "amount", "status", "currency", "merchant_id", "merchant_code", "merchant_name", "reseller_id", "reseller_code", "reseller_name", "provider_code", "client_bank_name", "client_name", "client_bank_account", "createdAt", "updatedAt", "type"];
    exportToCsv(transactions, {
      headers: HEADERS,
      filename: `deposits-${new Date().toISOString().slice(0, 10)}.csv`,
      getCellValue: (row, key) =>
        key === "merchant_name" ? (row.merchant_name ?? row.merchant?.name) :
          key === "reseller_name" ? (row.reseller_name ?? row.reseller?.name) :
            row[key],
    });
  }, [transactions]);

  return (
    <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6">

      {/* ── Header ── */}
      <div className="dep-hdr">
        <div className="dep-hdr-l">
          <div className="dep-live">
            <span className="dep-dot-wrap"><span className="dep-dot-ring" /><span className="dep-dot" /></span>
            <span className="dep-live-lbl">Live · 5s refresh</span>
          </div>
          <h1 className="dep-title">{t("deposits")}</h1>
          <p className="dep-count"><b>{totalTransactions.toLocaleString()}</b> {t("deposits") || "Deposits"}</p>
        </div>
        <div className="dep-chips">
          <div className="dep-chip dep-chip-c">
            <ArrowDownToLine className="dep-chip-ico" />
            <div><p className="dep-chip-lbl">{t("total") || "Total"}</p><p className="dep-chip-val">{stats.count.toLocaleString()}</p></div>
          </div>
          <div className="dep-chip dep-chip-e">
            <Layers className="dep-chip-ico" />
            <div><p className="dep-chip-lbl">{t("successful") || "Success"}</p><p className="dep-chip-val">{stats.successCount.toLocaleString()}</p></div>
          </div>
          <div className="dep-chip dep-chip-p">
            <Activity className="dep-chip-ico" />
            <div><p className="dep-chip-lbl">{t("pending") || "Pending"}</p><p className="dep-chip-val">{stats.pendingCount.toLocaleString()}</p></div>
          </div>
        </div>
      </div>

      {/* ── Filters Panel ── */}
      <div className="dep-panel">
        <div className="dep-panel-bar">
          <div className="dep-panel-bar-l">
            <div className="dep-panel-icon dep-panel-icon-c"><Activity /></div>
            <span className="dep-panel-title">{t("filters") || "Filters"}</span>
            <span className="dep-line" />
          </div>
          <button className="dep-export-btn" onClick={handleExport}>
            <ArrowDownToLine />{t("export") || "Export"}
          </button>
        </div>
        <div className="dep-filters-pad dep-filters">
          <TransactionFilters
            filters={filters}
            onFilterChange={handleFilter}
            merchants={merchants}
            providers={providers}
            onExport={handleExport}
            onReset={handleReset}
          />
        </div>
      </div>

      {/* ── Table Panel ── */}
      <div className="dep-panel">
        <div className="dep-panel-bar">
          <div className="dep-panel-bar-l">
            <div className="dep-panel-icon dep-panel-icon-b"><Activity /></div>
            <span className="dep-panel-title">{t("recentTransactions") || "Deposits"}</span>
          </div>
          {qrLoading && (
            <div className="dep-sync">
              <Sparkles className="dep-sync-ico" />
              <span className="dep-sync-lbl">Syncing…</span>
            </div>
          )}
        </div>
        <div className="dep-table-wrap">
          <TransactionTable
            transactions={paginated}
            isLoading={qrLoading}
            onView={setSelectedTransaction}
            isQRCode={true}
          />
        </div>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="dep-pag">
          <span className="dep-pag-info">
            <b>{(page * PAGE_SIZE + 1).toLocaleString()}–{Math.min((page + 1) * PAGE_SIZE, totalTransactions).toLocaleString()}</b>
            {" "}of{" "}<em>{totalTransactions.toLocaleString()}</em>
          </span>
          <div className="dep-pag-ctrl">
            <button className="dep-pag-btn" onClick={() => setPage(page - 1)} disabled={page === 0}><ChevronLeft /></button>
            {pills.map(p => (
              <button key={p} className={`dep-pag-pill${p === page ? " active" : ""}`} onClick={() => setPage(p)}>{p + 1}</button>
            ))}
            <button className="dep-pag-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}><ChevronRight /></button>
          </div>
        </div>
      )}

      {/* ── Transaction Detail Sheet (uses updated dark TransactionDetail) ── */}
      <TransactionDetail
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        isQRCode={true}
      />
    </div>
  );
}